import { canEditMessageByCurrentUser } from 'hooks/useCanEditMessage';
import { MessageV2DTO, PartnerDTO, TransactionState, UserDTO } from 'support/types';

/**
 * The edit flow can be entered for a DATA error fix; if it was, the message must still
 * carry a DATA error to remain editable. Non-error edit flows are unaffected.
 */
export const isStillEditableErrorState = (
  originalMessage: MessageV2DTO | undefined,
  latestMessage: MessageV2DTO | undefined,
): boolean => !isDataErrorFlow(originalMessage) || isDataErrorFlow(latestMessage);

export const isInFailedState = (message: MessageV2DTO | undefined): boolean =>
  message?.state === TransactionState.failed;

export const isEditableReview = (message: MessageV2DTO | undefined): boolean =>
  message?.state === TransactionState.pending_review && message?.review?.type === 'document_requires_manual_review';

export const isEditableMessageState = (message: MessageV2DTO | undefined): boolean =>
  isInFailedState(message) || isEditableReview(message);

/**
 * True only when we can prove the server copy is newer than the one being edited,
 * i.e. it changed underneath us. Missing timestamps are treated as "unchanged" — the
 * server's message_update_key optimistic lock remains the backstop.
 */
export const hasMessageAlreadyBeenUpdated = (
  originalMessage: MessageV2DTO | undefined,
  latestMessage: MessageV2DTO | undefined,
): boolean =>
  Boolean(
    originalMessage?.updated_at &&
    latestMessage?.updated_at &&
    new Date(latestMessage.updated_at).getTime() > new Date(originalMessage.updated_at).getTime(),
  );

/**
 * Last-line check run against the freshly refetched message right before submitting an edit:
 * the current user may still edit it, it is still in an editable error state, still in an
 * editable message state (FAILED or order-agent PENDING_REVIEW), and has not been changed by
 * anyone else since it was loaded.
 */
export const canSubmitMessageEdit = ({
  originalMessage,
  latestMessage,
  currentUser,
  currentPartner,
}: {
  originalMessage: MessageV2DTO | undefined;
  latestMessage: MessageV2DTO | undefined;
  currentUser: UserDTO | undefined;
  currentPartner: PartnerDTO | undefined;
}): boolean =>
  canEditMessageByCurrentUser(latestMessage, currentUser, currentPartner) &&
  isStillEditableErrorState(originalMessage, latestMessage) &&
  isEditableMessageState(latestMessage) &&
  !hasMessageAlreadyBeenUpdated(originalMessage, latestMessage);

const isDataErrorFlow = (message: MessageV2DTO | undefined): boolean => message?.blocking_error?.type === 'DATA';
